import React, { useState, useEffect } from "react";
import {
  Home,
  Briefcase,
  Image as ImageIcon,
  Mail,
  Building2,
  Landmark,
  FileText,
  PlusCircle,
  Paintbrush,
  Armchair,
} from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate, useLocation } from "react-router-dom";

import LogoImage from "../HomePage/Images/Logo.jpeg";

const navLinks = [
  { name: "Home", icon: Home, path: "/" },
  { name: "Services", icon: Briefcase, path: "/services" },
  { name: "Gallery", icon: ImageIcon, path: "/gallery" },
  { name: "Contact", icon: Mail, path: "/contact-us" },
];

const quickActions = [
  {
    name: "Property Management",
    icon: Building2,
    path: "/property-management",
  },
  {
    name: "New Projects",
    icon: Landmark,
    path: "/projects",
  },
  {
    name: "Rent / Sale",
    icon: Home,
    path: "/rent-sale",
  },
  {
    name: "Rental Agreement",
    icon: FileText,
    path: "/rental-agreements",
  },
  {
    name: "E Khata",
    icon: FileText,
    path: "/e-khata",
  },
  {
    name: "Bescom Transfer",
    icon: FileText,
    path: "/bescom-transfer",
  },
  {
    name: "Painting",
    icon: Paintbrush,
    path: "/painting-services",
  },
  {
    name: "Rental Furnitures",
    icon: Armchair,
    path: "/rental-furnitures",
  },
];

/* ── WhatsApp SVG Icon ───────────────────────── */
const WhatsAppIcon = ({ size = 18 }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 32 32"
    width={size}
    height={size}
    fill="currentColor"
  >
    <path d="M16.004 2.667C8.64 2.667 2.667 8.64 2.667 16c0 2.347.64 4.64 1.853 6.64L2.667 29.333l6.907-1.813A13.267 13.267 0 0 0 16.004 29.333c7.36 0 13.33-5.973 13.33-13.333 0-7.36-5.97-13.333-13.33-13.333Zm0 24.267a11 11 0 0 1-5.6-1.534l-.4-.24-4.107 1.08 1.094-4-.267-.413A11.02 11.02 0 0 1 4.987 16c0-6.08 4.947-11.027 11.017-11.027 6.08 0 11.027 4.947 11.027 11.027 0 6.08-4.947 11.027-11.027 11.027Zm6.053-8.24c-.333-.16-1.96-.96-2.267-1.067-.306-.107-.52-.16-.747.16-.213.32-.84 1.067-1.04 1.28-.187.213-.387.24-.72.08-.333-.16-1.413-.52-2.693-1.653-.987-.88-1.653-1.973-1.853-2.307-.187-.333-.02-.507.147-.667.147-.147.333-.387.507-.573.173-.187.213-.32.32-.533.107-.213.053-.4-.027-.56-.08-.16-.747-1.787-1.027-2.453-.267-.64-.547-.56-.747-.56h-.64c-.213 0-.56.08-.853.4-.293.32-1.12 1.093-1.12 2.667 0 1.573 1.147 3.093 1.307 3.307.16.213 2.24 3.44 5.44 4.827.76.32 1.36.507 1.813.653.76.24 1.453.2 2 .12.613-.093 1.88-.773 2.147-1.52.267-.747.267-1.387.187-1.52-.08-.133-.293-.213-.627-.373Z" />
  </svg>
);

/* ── Post Ad Icon ────────────────────────────── */
const PostAdButton = ({ onClick }) => (
  <motion.button
    whileHover={{ scale: 1.04 }}
    whileTap={{ scale: 0.97 }}
    onClick={onClick}
    className="group relative flex items-center gap-2 px-4 sm:px-5 py-2.5 rounded-full font-semibold text-sm sm:text-base border border-red-200 bg-white text-red-600 hover:bg-red-50 transition-all duration-300 shadow-sm hover:shadow-red-100 hover:border-red-300 overflow-hidden"
  >
    {/* animated shimmer */}
    <span className="absolute inset-0 -translate-x-full group-hover:translate-x-full transition-transform duration-700 bg-gradient-to-r from-transparent via-red-50/60 to-transparent pointer-events-none" />
    <PlusCircle
      size={17}
      className="text-red-500 group-hover:rotate-90 transition-transform duration-300"
    />
    <span className="hidden sm:inline tracking-tight">Post Ad</span>
    <span className="sm:hidden">Ad</span>
  </motion.button>
);

const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);

  const navigate = useNavigate();
  const location = useLocation();

  const getActiveName = () => {
    const p = location.pathname;
    if (p.startsWith("/services")) return "Services";
    if (p.startsWith("/gallery") || p.startsWith("/projects")) return "Gallery";
    if (p.startsWith("/contact-us")) return "Contact";
    if (p === "/") return "Home";
    return "";
  };

  const active = getActiveName();

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 10);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <>
      {/* ═══════════════ NAVBAR ═══════════════ */}
      <motion.header
        initial={{ y: -60, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.4 }}
        className={`sticky top-0 z-50 transition-all duration-300 ${
          scrolled
            ? "bg-white/70 backdrop-blur-2xl shadow-xl border-b border-gray-200"
            : "bg-white/60 backdrop-blur-xl"
        }`}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 h-16 sm:h-20 flex justify-between items-center gap-3">

          {/* ── LOGO ── */}
          <div
            onClick={() => navigate("/")}
            className="cursor-pointer flex items-center gap-3 shrink-0"
          >
            <img
              src={LogoImage}
              alt="logo"
              className="h-8 sm:h-10 w-auto object-contain"
            />
          </div>

          {/* ── NAV LINKS (desktop) ── */}
          <div className="hidden md:flex items-center">
            <div className="bg-white/80 backdrop-blur-xl border border-gray-200 shadow-md rounded-full p-2 flex items-center gap-1">
              {navLinks.map((link) => (
                <button
                  key={link.name}
                  onClick={() => {
                    navigate(link.path);
                  }}
                  className={`relative px-5 py-2.5 rounded-full text-sm font-semibold transition-all duration-300 ${
                    active === link.name
                      ? "bg-red-50 text-red-600 shadow-sm"
                      : "text-gray-700 hover:bg-gray-100 hover:text-red-600"
                  }`}
                >
                  {link.name}
                  {active === link.name && (
                    <motion.div
                      layoutId="navbar-pill"
                      className="absolute inset-0 rounded-full border border-red-100"
                      transition={{ type: "spring", stiffness: 300, damping: 25 }}
                    />
                  )}
                </button>
              ))}
            </div>
          </div>

          {/* ── ACTION BUTTONS ── */}
          <div className="flex items-center gap-2 sm:gap-3 shrink-0">

            {/* Post Ad */}
            <PostAdButton onClick={() => navigate("/post-ad")} />

            {/* WhatsApp — realistic green pill */}
            <motion.button
              whileHover={{ scale: 1.04 }}
              whileTap={{ scale: 0.97 }}
              onClick={() =>
                window.open("https://wa.me/message/MDG2ZSZNG6P6G1", "_blank")
              }
              className="group relative flex items-center gap-2 px-4 sm:px-5 py-2.5 rounded-full font-semibold text-sm sm:text-base bg-[#25D366] hover:bg-[#20C05C] text-white transition-all duration-300 shadow-lg shadow-[#25D366]/30 hover:shadow-[#25D366]/50 overflow-hidden"
            >
              {/* shimmer */}
              <span className="absolute inset-0 -translate-x-full group-hover:translate-x-full transition-transform duration-700 bg-gradient-to-r from-transparent via-white/20 to-transparent pointer-events-none" />

              <WhatsAppIcon size={18} />

              <span className="hidden sm:inline tracking-tight">WhatsApp</span>
              <span className="sm:hidden">Chat</span>

              {/* live green dot */}
              <span className="relative flex h-2 w-2">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-white opacity-60"></span>
                <span className="relative inline-flex rounded-full h-2 w-2 bg-white opacity-90"></span>
              </span>
            </motion.button>
          </div>
        </div>
      </motion.header>

      {/* ═══════════════ QUICK ACTION BAR ═══════════════ */}
      <div className="w-full bg-gradient-to-r from-red-700 via-red-600 to-red-500 text-white rounded-b-[28px] py-4 px-2 md:px-8 shadow-xl shadow-red-900/20 relative z-40 overflow-hidden">

        {/* subtle radial glows */}
        <div className="pointer-events-none absolute inset-0 opacity-30">
          <div className="absolute -top-12 left-8 w-56 h-56 bg-white/20 blur-3xl rounded-full" />
          <div className="absolute top-2 right-4 w-64 h-32 bg-black/30 blur-3xl rounded-full" />
          <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-80 h-10 bg-black/20 blur-2xl rounded-full" />
        </div>

        <div className="max-w-7xl mx-auto relative">
          <div className="grid grid-cols-4 sm:grid-cols-8 gap-1 sm:gap-2 w-full">
            {quickActions.map((item, index) => {
              const Icon = item.icon;
              return (
                <motion.button
                  key={item.name}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.04, duration: 0.3 }}
                  whileHover={{ y: -3 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => navigate(item.path)}
                  className="group flex flex-col items-center justify-start px-1 py-1 transition-all duration-300"
                >
                  {/* icon box */}
                  <div className="relative bg-white/10 backdrop-blur-md border border-white/15 group-hover:bg-white group-hover:shadow-lg group-hover:shadow-red-900/20 p-2.5 sm:p-3 rounded-2xl mb-2 transition-all duration-300">
                    <Icon
                      size={20}
                      className="sm:w-5 sm:h-5 text-white group-hover:text-red-600 transition-all duration-300"
                    />
                    {/* NEW badge for Painting & Rental Furnitures */}
                    {(item.name === "Painting" ||
                      item.name === "Rental Furnitures") && (
                      <span className="absolute -top-1.5 -right-1.5 bg-amber-400 text-amber-900 text-[8px] font-bold px-1 py-0.5 rounded-full leading-none shadow-sm">
                        NEW
                      </span>
                    )}
                  </div>

                  {/* label */}
                  <span className="text-[9px] sm:text-[11px] font-medium text-center leading-tight text-white/90 group-hover:text-white transition-colors duration-200 max-w-[60px] sm:max-w-full">
                    {item.name}
                  </span>
                </motion.button>
              );
            })}
          </div>
        </div>
      </div>

      {/* ═══════════════ MOBILE NAV ═══════════════ */}
      <div className="md:hidden fixed bottom-5 left-1/2 -translate-x-1/2 z-[60] w-[92%] max-w-[380px]">
        <div className="bg-white/90 backdrop-blur-2xl px-3 py-2 rounded-3xl shadow-2xl shadow-black/10 border border-gray-200 flex justify-around">
          {navLinks.map((link) => {
            const Icon = link.icon;
            const isActive = active === link.name;
            return (
              <button
                key={link.name}
                onClick={() => {
                  navigate(link.path);
                }}
                className="flex flex-col items-center justify-center w-16 h-14 transition-all duration-300"
              >
                <div
                  className={`w-10 h-10 rounded-2xl flex items-center justify-center transition-all duration-300 ${
                    isActive ? "bg-red-50 text-red-600" : "text-gray-500"
                  }`}
                >
                  <Icon size={20} />
                </div>
                <span
                  className={`text-[10px] mt-0.5 font-semibold transition-colors duration-200 ${
                    isActive ? "text-red-600" : "text-gray-400"
                  }`}
                >
                  {link.name}
                </span>
              </button>
            );
          })}
        </div>
      </div>

      {/* ═══════════════ SPACER ═══════════════ */}
      <div className="h-16 sm:h-20" />
    </>
  );
};

export default Navbar;